// =============================================================
// VARIANCE — The Executive Command Center
// File      : Layout.tsx
// Location  : /home/Applications/variance/web/src/components/Layout.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Fully responsive layout with 3 working shells.
//             Added Finance nav entry (Banknote icon) between
//             Vendors and Compliance, linking to /finance.
// =============================================================

import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Building2, FolderKanban, Users, FileText, Settings, LandPlot, Menu, X, ShieldCheck, TrendingUp, Banknote } from 'lucide-react';
import { applyTheme, getSavedTheme, getSavedShell, Theme, ShellType } from '../lib/theme';
import { useAuth } from '../lib/AuthContext';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { user, roles } = useAuth();

  const [shell, setShell] = useState<ShellType>('shell-classic');
  const [, setTheme] = useState<Theme>('theme-dark');
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();

  // Listen for custom event from Settings page to change layout instantly
  useEffect(() => {
    const handleShellChange = () => {
      setShell(getSavedShell());
    };
    window.addEventListener('shellChanged', handleShellChange);
    
    // Initial load
    setShell(getSavedShell());
    const savedTheme = getSavedTheme();
    setTheme(savedTheme);
    applyTheme(savedTheme);

    return () => window.removeEventListener('shellChanged', handleShellChange);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const allNavItems = [
    { name: 'Macro Vantage', path: '/', icon: <LayoutDashboard size={20} />, module: 'Cockpit' },
    { name: 'Companies', path: '/companies', icon: <Building2 size={20} />, module: 'Companies' },
    { name: 'Sales Hub', path: '/sales', icon: <TrendingUp size={20} />, module: 'Sales Hub' },
    { name: 'Projects', path: '/projects', icon: <FolderKanban size={20} />, module: 'Projects' },
    { name: 'Assets', path: '/assets', icon: <LandPlot size={20} />, module: 'Assets' },
    { name: 'Vendors', path: '/vendors', icon: <Users size={20} />, module: 'Vendors' },
    { name: 'Finance', path: '/finance', icon: <Banknote size={20} />, module: 'Finance' },
    { name: 'Compliance', path: '/compliance', icon: <ShieldCheck size={20} />, module: 'Compliance' },
    { name: 'HR', path: '/hr', icon: <FileText size={20} />, module: 'HR' },
    { name: 'Reports', path: '/reports', icon: <FileText size={20} />, module: 'Reports' },
    { name: 'Settings', path: '/settings', icon: <Settings size={20} />, module: 'Settings' },
  ];

  const navItems = allNavItems.filter(item => canAccess(item.module));

  const isActive = (path: string) => location.pathname === path;

  // Mobile Sidebar Component
  const MobileSidebar = () => (
    <>
      {isMobileOpen && (
        <>
          <div onClick={() => setIsMobileOpen(false)} className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-40 lg:hidden" />
          <aside className="fixed inset-y-0 left-0 w-72 z-50 flex flex-col shadow-2xl lg:hidden" style={{ backgroundColor: 'var(--bg-panel)', borderRight: '1px solid var(--border-subtle)' }}>
            <div className="p-4 flex items-center justify-between border-b" style={{ borderColor: 'var(--border-subtle)' }}>
              <div className="flex flex-col">
                <img src="/assets/images/cf_logo_right.png" alt="CogniFleet" className="h-8 mb-1 object-contain self-start" />
                <img src="/assets/images/variance_logo1.png" alt="VARIANCE" className="h-8 object-contain self-start" />
                <p className="text-[9px] mt-2 tracking-widest uppercase text-center w-full" style={{ color: 'var(--text-secondary)' }}>
                  Focus on What Matters
                </p>
              </div>
              <button onClick={() => setIsMobileOpen(false)} className="p-2 rounded-full hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ color: 'var(--text-secondary)' }}>
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-4 space-y-2">
              {navItems.map(item => (
                <Link key={item.name} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors hover:bg-[var(--bg-panel-alt)] ${isActive(item.path) ? 'bg-[var(--bg-panel-alt)]' : ''}`} style={{ color: isActive(item.path) ? 'var(--brand-accent)' : 'var(--text-secondary)' }}>
                  {item.icon} {item.name}
                </Link>
              ))}
            </nav>
          </aside>
        </>
      )}
    </>
  );

  // --- SHELL A: CLASSIC SIDEBAR ---
  if (shell === 'shell-classic') {
    return (
      <div className="flex h-screen w-full overflow-hidden" style={{ backgroundColor: 'var(--bg-canvas)' }}>
        <MobileSidebar />
        
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex w-64 flex-col" style={{ backgroundColor: 'var(--bg-panel)', borderRight: '1px solid var(--border-subtle)' }}>
          <div className="p-4 flex flex-col items-center border-b" style={{ borderColor: 'var(--border-subtle)' }}>
            <img src="/assets/images/cf_logo_right.png" alt="CogniFleet" className="h-14 mb-1 object-contain" />
            <img src="/assets/images/variance_logo1.png" alt="VARIANCE" className="w-full max-w-[180px] h-auto object-contain" />
            <p className="text-[10px] mt-2 tracking-widest uppercase text-center w-full" style={{ color: 'var(--text-secondary)' }}>
              Focus on What Matters
            </p>
          </div>
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navItems.map(item => (
              <Link key={item.name} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors hover:bg-[var(--bg-panel-alt)] ${isActive(item.path) ? 'bg-[var(--bg-panel-alt)]' : ''}`} style={{ color: isActive(item.path) ? 'var(--brand-accent)' : 'var(--text-secondary)' }}>
                {item.icon} {item.name}
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 flex flex-col">
          <header className="h-16 flex items-center px-4 lg:px-6 border-b" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
            <button onClick={() => setIsMobileOpen(true)} className="mr-4 p-2 rounded-lg hover:bg-[var(--bg-panel-alt)] lg:hidden" style={{ color: 'var(--text-primary)' }}>
              <Menu size={24} />
            </button>
            <h2 className="text-lg font-semibold hidden sm:block" style={{ color: 'var(--text-primary)' }}>Dashboard</h2>
            <div className="ml-auto flex items-center gap-4">
              <div className="flex items-center gap-2">
  	        <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>
    		  {user?.name?.charAt(0) || 'U'}
  		</div>
  		<div className="hidden md:flex flex-col">
    		  <span className="text-sm font-semibold text-white leading-tight">Welcome, {user?.name || 'User'}</span>
    		  <span className="text-[10px] text-gray-400 uppercase tracking-wider">{roles[0] || 'No Role'}</span>
  		</div>
	      </div>
            </div>
          </header>
          <div className="flex-1 overflow-auto p-4 lg:p-6">{children}</div>
        </main>
      </div>
    );
  }

  // --- SHELL B: IMMERSIVE ICON RAIL ---
  if (shell === 'shell-immersive') {
    return (
      <div className="flex h-screen w-full overflow-hidden" style={{ backgroundColor: 'var(--bg-canvas)' }}>
        <MobileSidebar />
        
        <aside className="hidden lg:flex w-20 flex-col items-center py-6 border-r" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <img src="/assets/images/variance_logo1.png" alt="V" className="w-10 h-auto object-contain mb-8" />
          <nav className="flex-1 flex flex-col gap-4 w-full px-2 overflow-y-auto">
            {navItems.map(item => (
              <Link key={item.name} to={item.path} title={item.name} className={`w-full aspect-square flex items-center justify-center rounded-xl transition-all ${isActive(item.path) ? 'bg-[var(--bg-panel-alt)]' : 'hover:bg-[var(--bg-panel-alt)]'}`} style={{ color: isActive(item.path) ? 'var(--brand-accent)' : 'var(--text-secondary)' }}>
                {item.icon}
              </Link>
            ))}
          </nav>
        </aside>

        <main className="flex-1 flex flex-col">
          <header className="h-16 flex items-center px-4 lg:px-8">
            <button onClick={() => setIsMobileOpen(true)} className="mr-4 p-2 rounded-lg hover:bg-[var(--bg-panel-alt)] lg:hidden" style={{ color: 'var(--text-primary)' }}>
              <Menu size={24} />
            </button>
            <h2 className="text-lg font-semibold opacity-50 hidden sm:block" style={{ color: 'var(--text-primary)' }}>Dashboard</h2>
            <div className="ml-auto flex items-center gap-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>C</div>
            </div>
          </header>
          <div className="flex-1 overflow-auto p-4 lg:p-8">{children}</div>
        </main>
      </div>
    );
  }

  // --- SHELL C: TOP NAV EXECUTIVE ---
  return (
    <div className="flex flex-col h-screen w-full overflow-hidden" style={{ backgroundColor: 'var(--bg-canvas)' }}>
      <MobileSidebar />
      
      <header className="h-16 flex items-center justify-between px-6 border-b" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-4">
            <img src="/assets/images/cf_logo_right.png" alt="CogniFleet" className="h-8 object-contain" />
            <div className="w-px h-6" style={{ backgroundColor: 'var(--border-subtle)' }}></div>
            <img src="/assets/images/variance_logo1.png" alt="VARIANCE" className="h-6 object-contain" />
          </div>
          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map(item => (
              <Link key={item.name} to={item.path} className={`text-sm font-medium transition-colors hover:text-[var(--brand-accent)] ${isActive(item.path) ? 'text-[var(--brand-accent)]' : ''}`} style={{ color: isActive(item.path) ? 'var(--brand-accent)' : 'var(--text-secondary)' }}>
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => setIsMobileOpen(true)} className="p-2 rounded-lg hover:bg-[var(--bg-panel-alt)] lg:hidden" style={{ color: 'var(--text-primary)' }}>
            <Menu size={24} />
          </button>
          <span className="text-sm hidden md:block" style={{ color: 'var(--text-secondary)' }}>Welcome, Leader</span>
          <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>C</div>
        </div>
      </header>
      <main className="flex-1 overflow-auto p-6 lg:p-8">{children}</main>
    </div>
  );
}
